import Head from 'next/head';
import PropTypes from 'prop-types';
import {
  SEO_DEFAULTS,
  getImageMimeType,
  getAbsoluteSocialImageUrl,
  getAbsoluteUrl,
  getFacebookAppId,
  resolveImageValue,
} from '@/lib/seo';

const toHrefLang = (locale = '') => String(locale || '').replace('_', '-');

export default function SeoHead({
  siteName,
  title,
  openGraphTitle,
  openGraphDescription,
  twitterTitle,
  twitterDescription,
  description,
  path = '',
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  imageType,
  type = 'website',
  noIndex = false,
  metaTags = [],
  structuredData,
}) {
  const normalizeMetaText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

  const resolvedTitle = normalizeMetaText(title || SEO_DEFAULTS.defaultTitle);
  const resolvedOpenGraphTitle = normalizeMetaText(openGraphTitle || resolvedTitle);
  const resolvedDescription = description || SEO_DEFAULTS.defaultDescription;
  const resolvedOpenGraphDescription = normalizeMetaText(openGraphDescription || resolvedDescription);
  const resolvedTwitterTitle = normalizeMetaText(twitterTitle || resolvedOpenGraphTitle);
  const resolvedTwitterDescription = normalizeMetaText(twitterDescription || resolvedOpenGraphDescription);
  const canonicalUrl = getAbsoluteUrl(path);
  const resolvedImage = resolveImageValue(image) || SEO_DEFAULTS.defaultImage;
  const imageUrl = getAbsoluteSocialImageUrl(resolvedImage);
  const resolvedImageType = imageType || getImageMimeType(imageUrl || resolvedImage);
  const resolvedSiteName = siteName || SEO_DEFAULTS.siteName;
  const facebookAppId = getFacebookAppId();
  const robots = noIndex ? 'noindex, nofollow' : 'index, follow';
  const hrefLang = toHrefLang(SEO_DEFAULTS.locale);

  return (
    <Head>
      <title>{resolvedTitle}</title>
      <meta key="description" name="description" content={resolvedDescription} />
      <meta key="robots" name="robots" content={robots} />
      <meta key="theme-color" name="theme-color" content={SEO_DEFAULTS.themeColor} />

      <meta key="og:type" property="og:type" content={type} />
      <meta key="og:site_name" property="og:site_name" content={resolvedSiteName} />
      <meta key="og:locale" property="og:locale" content={SEO_DEFAULTS.locale} />
      {facebookAppId ? <meta key="fb:app_id" property="fb:app_id" content={facebookAppId} /> : null}
      <meta key="og:title" property="og:title" content={resolvedOpenGraphTitle} />
      <meta key="og:description" property="og:description" content={resolvedOpenGraphDescription} />

      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:title" name="twitter:title" content={resolvedTwitterTitle} />
      <meta key="twitter:description" name="twitter:description" content={resolvedTwitterDescription} />
      <meta key="twitter:site" name="twitter:site" content={SEO_DEFAULTS.twitterHandle} />

      {canonicalUrl ? <link key="canonical" rel="canonical" href={canonicalUrl} /> : null}
      {canonicalUrl ? <link key="alternate-locale" rel="alternate" hrefLang={hrefLang} href={canonicalUrl} /> : null}
      {canonicalUrl ? <link key="alternate-default" rel="alternate" hrefLang="x-default" href={canonicalUrl} /> : null}
      {canonicalUrl ? <meta key="og:url" property="og:url" content={canonicalUrl} /> : null}
      {canonicalUrl ? <meta key="twitter:url" name="twitter:url" content={canonicalUrl} /> : null}
      {imageUrl ? <meta key="og:image" property="og:image" content={imageUrl} /> : null}
      {imageUrl ? <meta key="og:image:url" property="og:image:url" content={imageUrl} /> : null}
      {imageUrl ? <meta key="og:image:secure_url" property="og:image:secure_url" content={imageUrl} /> : null}
      {imageUrl ? <meta key="og:image:type" property="og:image:type" content={resolvedImageType} /> : null}
      {imageUrl && imageWidth ? <meta key="og:image:width" property="og:image:width" content={String(imageWidth)} /> : null}
      {imageUrl && imageHeight ? <meta key="og:image:height" property="og:image:height" content={String(imageHeight)} /> : null}
      {imageAlt ? <meta key="og:image:alt" property="og:image:alt" content={imageAlt} /> : null}
      {imageUrl ? <meta key="twitter:image" name="twitter:image" content={imageUrl} /> : null}
      {imageAlt ? <meta key="twitter:image:alt" name="twitter:image:alt" content={imageAlt} /> : null}
      {metaTags.map((tag, index) => {
        if (!tag?.content || (!tag?.name && !tag?.property)) {
          return null;
        }

        return (
          <meta
            key={`${tag.property || tag.name}-${index}`}
            {...(tag.property ? { property: tag.property } : { name: tag.name })}
            content={String(tag.content)}
          />
        );
      })}
      {structuredData ? (
        <script
          key="structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      ) : null}
    </Head>
  );
}

SeoHead.propTypes = {
  siteName: PropTypes.string,
  title: PropTypes.string,
  openGraphTitle: PropTypes.string,
  openGraphDescription: PropTypes.string,
  twitterTitle: PropTypes.string,
  twitterDescription: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  imageWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  imageHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  imageType: PropTypes.string,
  type: PropTypes.string,
  noIndex: PropTypes.bool,
  metaTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      property: PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  structuredData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
